import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

function norm(s) {
  return (s || "").toString().toLowerCase();
}

/**
 * Searchable single-select modal for Microsoft calendars (same UX family as ModernProductSelector).
 */
export function MicrosoftEventCalendarSelectorModal({
  open,
  onClose,
  calendars = [],
  selectedId,
  onSelect,
  isLoading = false,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) setSearchQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = norm(searchQuery).trim();
    if (!q) return calendars;
    return calendars.filter((c) => {
      const name = norm(c.name);
      const owner = norm(c.owner_email);
      return name.includes(q) || owner.includes(q);
    });
  }, [calendars, searchQuery]);

  const pick = (cal) => {
    onSelect?.(cal);
    onClose?.();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[2100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative flex max-h-[85vh] w-full max-w-lg transform flex-col overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all">
                <div className="flex-shrink-0 border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      {t("microsoft.user.calendar_modal_title")}
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-label={t("microsoft.user.calendar_modal_close")}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    {t("microsoft.user.calendar_modal_hint")}
                  </p>
                  <div className="relative mt-3">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t("microsoft.user.calendar_search_placeholder")}
                      className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-9 text-sm placeholder:text-gray-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      autoFocus
                    />
                    {searchQuery ? (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                  {isLoading && calendars.length === 0 ? (
                    <div className="flex h-48 items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-sky-600" />
                    </div>
                  ) : null}

                  {!isLoading || calendars.length > 0 ? (
                    <ul className="divide-y divide-gray-100">
                      <li>
                        <button
                          type="button"
                          onClick={() => pick(null)}
                          className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 ${
                            !selectedId ? "bg-sky-50" : ""
                          }`}
                        >
                          <span
                            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                              !selectedId
                                ? "border-sky-600 bg-sky-600"
                                : "border-gray-300"
                            }`}
                          >
                            {!selectedId ? (
                              <CheckIcon className="h-3.5 w-3.5 text-white" />
                            ) : null}
                          </span>
                          <span className="font-medium text-gray-700">
                            {t("microsoft.user.calendar_none")}
                          </span>
                        </button>
                      </li>
                      {filtered.map((c) => {
                        const selected = c.id === selectedId;
                        return (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => pick(c)}
                              className={`flex w-full items-start gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 ${
                                selected ? "bg-sky-50" : ""
                              }`}
                            >
                              <span
                                className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                                  selected
                                    ? "border-sky-600 bg-sky-600"
                                    : "border-gray-300"
                                }`}
                              >
                                {selected ? (
                                  <CheckIcon className="h-3.5 w-3.5 text-white" />
                                ) : null}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-medium text-gray-900">
                                  {c.name || c.id}
                                </span>
                                <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                                  {c.owner_email ? (
                                    <span className="truncate">{c.owner_email}</span>
                                  ) : null}
                                  {c.owned_by_me ? (
                                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                                      {t("microsoft.user.calendar_badge_own")}
                                    </span>
                                  ) : (
                                    <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">
                                      {t("microsoft.user.calendar_badge_shared")}
                                    </span>
                                  )}
                                  {c.can_edit === false ? (
                                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                                      {t("microsoft.user.calendar_badge_readonly")}
                                    </span>
                                  ) : null}
                                </span>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}

                  {!isLoading &&
                  calendars.length > 0 &&
                  filtered.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-gray-500">
                      {t("microsoft.user.calendar_search_empty")}
                    </p>
                  ) : null}

                  {!isLoading && calendars.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-gray-500">
                      {t("microsoft.user.calendar_modal_empty")}
                    </p>
                  ) : null}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
